/**
 * API client.
 *
 * Types mirror the C# records in Swo.Assessment.Api. They are hand written
 * rather than generated, so if the server contract changes this file must
 * change with it. The alternative, generating from the OpenAPI document at
 * build time, is worth doing once the contract settles; doing it now would add
 * a build step to a shape that is still moving.
 */

export type MsPillar =
  | 'identity'
  | 'endpoints'
  | 'data'
  | 'apps'
  | 'infrastructure'
  | 'network';

export type Severity = 'Critical' | 'High' | 'Medium' | 'Low' | 'Info';

export type ResolutionTier = 'selected' | 'fallback' | 'unmapped';

export type Relationship =
  | 'equivalent'
  | 'subset-of'
  | 'superset-of'
  | 'intersects-with'
  | 'not-related';

export interface Framework {
  /** Whether an operator has stated they hold a commercial licence. Not verified. */
  commercialLicenceAsserted: boolean;
  licenceAssertedBy: string | null;
  licenceAssertedUtc: string | null;
  licenceReference: string | null;
  frameworkId: string;
  name: string;
  /** Null where no version has been established. */
  version: string | null;
  /** publisher, secondary or unknown: how far the version can be trusted. */
  versionSource: 'publisher' | 'secondary' | 'unknown';
  publisher: string;
  jurisdiction: string;
  sector: string | null;
  licenceCode: string;
  contentMayShip: boolean;
  requiresLicence: boolean;
  isFallbackTier: boolean;
  /** False where the framework is registered but no content has been imported. */
  contentLoaded: boolean;
  controlCount: number;
  /** Null means the version has not been checked against the publisher. */
  versionVerifiedUtc: string | null;
  sourceUrl: string | null;
}

export interface Primitive {
  primitiveId: string;
  title: string;
  statement: string;
  msPillar: MsPillar;
  cisaPillar: string | null;
  mappingCount: number;
}

export interface TenantFramework {
  frameworkId: string;
  name: string;
  priority: number;
}

export interface Tenant {
  tenantId: string;
  entraTenantId: string;
  displayName: string;
  homeRegion: string;
  primaryDomain: string | null;
  consentGranted: boolean;
  consentGrantedUtc: string | null;
  frameworks: TenantFramework[];
}

export interface RunDetail extends RunSummary {
  /** Last step the collector reported, while it is still running. */
  progress: string | null;
  triggeredBy: string;
  triggeredByUser: string | null;
}

export interface RunSummary {
  runId: string;
  tenantId: string;
  startedUtc: string;
  completedUtc: string | null;
  status: 'running' | 'complete' | 'failed';
  collectorVersion: string | null;
  findingCount: number;
}

export interface FindingResource {
  resourceType: string;
  name: string;
  identifier: string | null;
  held: string | null;
  context: string | null;
}

export type DispositionStatus =
  | 'open'
  | 'in-progress'
  | 'remediated'
  | 'accepted-risk'
  | 'not-applicable';

export interface Disposition {
  checkId: string;
  status: DispositionStatus;
  owner: string | null;
  targetDate: string | null;
  note: string | null;
  updatedUtc: string;
  updatedBy: string | null;
}

export interface Finding {
  findingId: number;
  checkId: string;
  severity: Severity;
  title: string;
  detail: string | null;
  recommendation: string | null;
  area: string;
  primitiveId: string | null;
  resources: FindingResource[];
  /** Where the change is made. */
  remediationPortal: string | null;
  /** Ordered steps. Empty where no plan is registered for this check. */
  remediationSteps: string[];
  /** What the customer has decided. Null where nothing has been recorded. */
  disposition: Disposition | null;
}

export interface ResolvedControl {
  frameworkId: string;
  frameworkName: string;
  controlRef: string;
  /** Null where the framework's content is not licensed to ship, or not imported. */
  controlTitle: string | null;
  relationship: Relationship;
  confidence: string;
  priority: number | null;
}

export interface Resolution {
  primitiveId: string | null;
  tier: ResolutionTier;
  explanation: string;
  controls: ResolvedControl[];
}

export interface Coverage {
  frameworkId: string;
  frameworkName: string;
  mapped: number;
  unmapped: number;
  unmappedPrimitives: string[];
  byRelationship: Record<string, number>;
}

export interface StampInfo {
  region: string;
  displayName: string;
  note: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type TokenProvider = () => Promise<string | null>;

export interface Control {
  controlRef: string;
  parentRef: string | null;
  title: string | null;
  description: string | null;
  implementationGroups: string[];
  /** Publisher specific fields, passed through as imported. */
  attributes: Record<string, unknown> | null;
}

export interface ControlPage {
  frameworkId: string;
  total: number;
  skip: number;
  take: number;
  controls: Control[];
}

export type ControlCoverageStatus =
  | 'findings'
  | 'clear'
  /** A check maps to it, but this collection could not see the data. */
  | 'not-available'
  /** A person has recorded that this tool cannot reach it. */
  | 'not-applicable'
  | 'not-assessed';

export interface CoverageFinding {
  findingId: number;
  checkId: string;
  severity: Severity;
  title: string;
  dispositionStatus: DispositionStatus | null;
}

export interface ControlCoverage {
  controlRef: string;
  title: string | null;
  description: string | null;
  status: ControlCoverageStatus;
  /** Which collectors were unavailable, and what the collector said. */
  unavailableReason: string | null;
  primitiveIds: string[];
  findings: CoverageFinding[];
}

export interface FrameworkCoverageSummary {
  frameworkId: string;
  frameworkName: string;
  tenantId: string;
  runId: string;
  totalControls: number;
  withFindings: number;
  clear: number;
  notAssessed: number;
  notAvailable: number;
  notApplicable: number;
  runStatus: string;
}

export interface FrameworkCoverage {
  summary: FrameworkCoverageSummary;
  total: number;
  skip: number;
  controls: ControlCoverage[];
}

export type ReadinessStatus = 'ready' | 'partial' | 'blocked' | 'unknown' | 'manual';

export interface ReadinessCheck {
  id: string;
  title: string;
  status: ReadinessStatus;
  detail: string;
  guidance: string | null;
  items: { name: string; unlocks: string }[] | null;
}

export interface Readiness {
  tenantId: string;
  displayName: string;
  entraTenantId: string;
  overall: ReadinessStatus;
  checks: ReadinessCheck[];
  consentUrl: string;
  portalUrl: string;
  exchangeCommand: string;
}

export interface FrameworkGuide {
  summary: string;
  howToRead: string;
  groupings: { label: string; meaning: string }[];
  caution?: string;
}

export interface FrameworkSource {
  frameworkId: string;
  name: string;
  format: string;
  licence: string;
  autoFetch: boolean;
  authoritativeUrl: string | null;
  verifiedUtc: string | null;
}

export interface FetchResult {
  frameworkId: string;
  version: string;
  lastModified: string | null;
  controlCount: number;
  warnings: string[];
}

export interface ImportResult {
  frameworkId: string;
  controlCount: number;
  safeguardCount: number;
  warnings: string[];
}

export class AssessmentApi {
  constructor(
    private readonly baseUrl: string,
    private readonly getToken: TokenProvider,
  ) {}

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const token = await this.getToken();
    const headers = new Headers(init?.headers);
    headers.set('Accept', 'application/json');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    // FormData sets its own Content-Type including the multipart boundary.
    // Forcing application/json here produces a body the server cannot parse,
    // and the failure is a confusing 400 rather than anything that names the
    // cause.
    const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData;
    if (init?.body && !isFormData && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    const response = await fetch(`${this.baseUrl}${path}`, { ...init, headers });

    if (!response.ok) {
      // Surface the server's own message where it gave one. The API returns
      // problem details for the consent and residency refusals, and those
      // explanations are more useful than a status code.
      let detail: string | undefined;
      try {
        const body = await response.json();
        detail = body?.detail ?? body?.error ?? body?.title;
      } catch {
        detail = undefined;
      }
      throw new ApiError(
        response.status,
        detail ?? `Request failed with status ${response.status}`,
        detail,
      );
    }

    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  getStamp = () => this.request<StampInfo>('/api/stamp');
  getFrameworks = () => this.request<Framework[]>('/api/catalogue/frameworks');
  getPrimitives = (msPillar?: MsPillar) =>
    this.request<Primitive[]>(
      `/api/catalogue/primitives${msPillar ? `?msPillar=${msPillar}` : ''}`,
    );
  getCoverage = (frameworkId: string) =>
    this.request<Coverage>(`/api/catalogue/frameworks/${frameworkId}/coverage`);

  /**
   * Uploads a licensed framework workbook.
   *
   * Multipart, so Content-Type is left unset: the browser must set it itself
   * to include the boundary marker, and overriding it produces a body the
   * server cannot parse.
   */
  importFramework = (
    frameworkId: string,
    file: File,
    attribution: string,
    commercialLicence: boolean,
  ) => {
    const body = new FormData();
    body.append('file', file);
    body.append('attribution', attribution);
    body.append('commercialLicence', String(commercialLicence));
    return this.request<ImportResult>(
      `/api/catalogue/frameworks/${encodeURIComponent(frameworkId)}/import`,
      { method: 'POST', body },
    );
  };

  assertLicence = (frameworkId: string, held: boolean, reference?: string | null) =>
    this.request<{ frameworkId: string; commercialLicenceAsserted: boolean; note: string }>(
      `/api/catalogue/frameworks/${encodeURIComponent(frameworkId)}/licence`,
      { method: 'PUT', body: JSON.stringify({ held, reference: reference ?? null }) },
    );

  getControls = (frameworkId: string, opts: { search?: string; skip?: number; take?: number } = {}) => {
    const q = new URLSearchParams();
    if (opts.search) q.set('search', opts.search);
    q.set('skip', String(opts.skip ?? 0));
    q.set('take', String(opts.take ?? 50));
    return this.request<ControlPage>(
      `/api/catalogue/frameworks/${encodeURIComponent(frameworkId)}/controls?${q}`);
  };

  setApplicability = (frameworkId: string, controlRef: string,
                      applicability: 'out-of-scope' | 'applicable', reason: string) =>
    this.request<void>(
      `/api/catalogue/frameworks/${encodeURIComponent(frameworkId)}/controls/${encodeURIComponent(controlRef)}/applicability`,
      { method: 'PUT', body: JSON.stringify({ applicability, reason }) });

  getFrameworkCoverage = (
    tenantId: string, runId: string, frameworkId: string,
    opts: { status?: string; skip?: number; take?: number } = {},
  ) => {
    const q = new URLSearchParams();
    if (opts.status) q.set('status', opts.status);
    q.set('skip', String(opts.skip ?? 0));
    q.set('take', String(opts.take ?? 50));
    return this.request<FrameworkCoverage>(
      `/api/tenants/${tenantId}/runs/${runId}/coverage/${encodeURIComponent(frameworkId)}?${q}`);
  };

  getDerivable = () =>
    this.request<{ frameworkId: string; scfColumn: string }[]>('/api/catalogue/derivable');

  deriveFramework = (frameworkId: string) =>
    this.request<{ frameworkId: string; sectionCount: number;
                   sourceControlCount: number; warnings: string[] }>(
      `/api/catalogue/frameworks/${encodeURIComponent(frameworkId)}/derive`,
      { method: 'POST' });

  getReadiness = (tenantId: string) =>
    this.request<Readiness>(`/api/tenants/${tenantId}/readiness`);

  getGuides = () =>
    this.request<{ guides: Record<string, FrameworkGuide> }>('/api/catalogue/guides');

  getFrameworkSources = () =>
    this.request<FrameworkSource[]>('/api/catalogue/sources');

  fetchFramework = (frameworkId: string) =>
    this.request<FetchResult>(
      `/api/catalogue/frameworks/${encodeURIComponent(frameworkId)}/fetch`,
      { method: 'POST' },
    );

  setTenantFrameworks = (tenantId: string, frameworks: TenantFramework[]) =>
    this.request<void>(`/api/tenants/${tenantId}/frameworks`, {
      method: 'PUT',
      body: JSON.stringify({ frameworks }),
    });

  createTenant = (body: { entraTenantId: string; displayName: string; primaryDomain?: string | null }) =>
    this.request<Tenant>('/api/tenants', { method: 'POST', body: JSON.stringify(body) });

  recordConsent = (tenantId: string) =>
    this.request<void>(`/api/tenants/${tenantId}/consent-granted`, { method: 'POST' });

  getConsentUrl = (tenantId: string) =>
    this.request<{ url: string; state: string; guidance: string }>(
      `/api/tenants/${tenantId}/consent-url`);

  getTenants = () => this.request<Tenant[]>('/api/tenants');
  getTenant = (id: string) => this.request<Tenant>(`/api/tenants/${id}`);
  startScan = (tenantId: string) =>
    this.request<{ runId: string; strategy: string; message: string }>(
      `/api/tenants/${tenantId}/runs/scan`, { method: 'POST' });

  getRun = (runId: string) => this.request<RunDetail>(`/api/runs/${runId}`);

  getRuns = (tenantId: string) =>
    this.request<RunSummary[]>(`/api/tenants/${tenantId}/runs`);
  getFindings = (runId: string, severity?: Severity) =>
    this.request<Finding[]>(
      `/api/runs/${runId}/findings${severity ? `?severity=${severity}` : ''}`,
    );
  resolve = (tenantId: string, primitiveId: string) =>
    this.request<Resolution>(`/api/tenants/${tenantId}/resolve/${primitiveId}`);

  setDisposition = (tenantId: string, checkId: string, body: {
    status: DispositionStatus; owner?: string | null;
    targetDate?: string | null; note?: string | null;
  }) =>
    this.request<Disposition>(
      `/api/tenants/${tenantId}/dispositions/${encodeURIComponent(checkId)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    );
}
